/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/react-hooks';
import styled from 'styled-components';

import Form from 'LegacyWeployDesignLanguage/Form';
import Toast from 'LegacyWeployDesignLanguage/Toast';
import { ContentContainer } from 'LegacyWeployDesignLanguage/layout';
import Theme from 'LegacyWeployDesignLanguage/Theme';
import { PasswordField } from 'LegacyWeployDesignLanguage/inputs/TextField';
import Button from 'WeployDesignLanguage/buttons/Button';

import TextField from '../../../components/weployer/common/TextField';

import { DASHBOARD_PATH, getInternalPath } from '../pathHelpers';

import { formatErrors } from '../../../utils/graphql';
import { weployerDetailsStorage } from '../../../utils/storage';
import { LOGIN, queryParams } from './graphql/queries';
import WeployLogoSvg from '../../../../../assets/images/logo-green.svg';

const TOAST_AUTO_HIDE_DURATION = 2000;

const StyledContentContainer = styled(ContentContainer)`
  padding-top: ${props => props.theme.space.single};
`;

const LoginPane = styled.div`
  display: block;
  margin: 100px auto 0;
  max-width: 400px;
`;

const StyledForm = styled(Form)`
  text-align: center;
`;

const LogoContainer = styled.div`
  text-align: center;
  margin-bottom: 80px;
`;

const Logo = styled.img`
  width: 50%;
  margin: 0 auto;
`;

const FieldContainer = styled.div`
  margin: ${props => props.theme.space.double} 0;
`;

const ButtonWrapper = styled.div`
  margin: 2em auto 0;
  width: 120px;
`;

const StyledLink = styled.a`
  color: ${props => props.secondary && Theme.colors.iconColor};
  font-weight: lighter !important;
`;

const StyledLinkContainer = styled.div`
  margin: ${Theme.space.single} 0 ${Theme.space.double};
`;

const Login = ({ applicationConfig: { railsPaths } }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [backendErrorMessage, setBackendErrorMessage] = useState('');
  const [backendSuccessMessage, setBackendSuccessMessage] = useState('');

  const setLocation = (path) => { window.location.href = path; };

  const handleSuccess = ({ login }) => {
    const defaultPath = getInternalPath({ pathName: DASHBOARD_PATH });
    const url = !railsPaths.previousLocation ? defaultPath : railsPaths.previousLocation;

    // These logs are for debugging purpose when users having trouble logging in
    // eslint-disable-next-line no-console
    console.log(`defaultPath: ${defaultPath}`);
    // eslint-disable-next-line no-console
    console.log(`railsPaths.previousLocation: ${railsPaths.previousLocation}`);

    const { userId, currentOrganisationId: organisationId } = login;
    weployerDetailsStorage.setItem({ userId, organisationId });

    window.location.href = url;
  };

  const handleError = (error) => {
    const errorMessage = formatErrors(error);
    setBackendErrorMessage(errorMessage);
  };

  const [login, { loading }] = useMutation(LOGIN, {
    variables: queryParams(email, password),
    onCompleted: handleSuccess,
    onError: handleError,
  });

  return (
    <Fragment>
      <Toast
        key="error-toast"
        open={!!backendErrorMessage}
        message={backendErrorMessage}
        type={Toast.ERROR}
        onDismiss={() => setBackendErrorMessage('')}
        autoHideDuration={TOAST_AUTO_HIDE_DURATION}
      />
      <Toast
        key="success-toast"
        open={!!backendSuccessMessage}
        message={backendSuccessMessage}
        type={Toast.INFO}
        onDismiss={() => setBackendSuccessMessage('')}
        autoHideDuration={TOAST_AUTO_HIDE_DURATION}
      />
      <StyledContentContainer>
        <LoginPane>
          <LogoContainer>
            <Logo src={WeployLogoSvg} alt="Weploy Logo" />
            <h6>Quality support staff to your door, quickly</h6>
          </LogoContainer>
          <h5>Log in</h5>
          { loading &&
            <h5>Loading...</h5>
          }
          <StyledForm onSubmit={(event) => {
            event.preventDefault();
            login();
            }}
          >
            <FieldContainer>
              <TextField
                id="email"
                name="Email"
                value={email}
                onChange={event => setEmail(event.target.value)}
                placeholder="Email"
                type="email"
                variant="standard"
              />
            </FieldContainer>
            <FieldContainer>
              <PasswordField
                id="password"
                name="Password"
                value={password}
                onChange={event => (setPassword(event.target.value))}
                placeholder="Password"
                type="password"
                variant="standard"
              />
            </FieldContainer>
            <ButtonWrapper>
              <Button type="submit" >
                Log in
              </Button>
            </ButtonWrapper>
            <StyledLinkContainer><StyledLink secondary href="#" onClick={() => setLocation(railsPaths.forgotPassword)}>Forgot your password?</StyledLink></StyledLinkContainer>
            <Button appearance={Button.TEXT} onClick={() => setLocation(railsPaths.signUp)}>
              Sign up and start hiring
            </Button>
          </StyledForm>
        </LoginPane>
      </StyledContentContainer>
    </Fragment>
  );
};

Login.propTypes = {
  applicationConfig: PropTypes.shape({
    railsPaths: PropTypes.object,
    features: PropTypes.object,
  }),
};

Login.defaultProps = {
  applicationConfig: {},
};

export default Login;
